import { useId } from "react";

/**
 * The Orbit wordmark -- the word with the orbit trail around the O -- in its
 * own colours: brass trail, warm-white letters on a dark ground, near-black
 * letters on a light one. Inline SVG rather than an <img> so the gradient
 * and glow survive and the two tones share one set of paths. Letters are
 * vector outlines, so no font is needed.
 *
 * Built from Orbit Custom/assets/brand/svg/orbit-wordmark-on-dark.svg and
 * -on-light.svg (2026-10-15), which differ only in the three fills below. Do
 * not hand-edit the paths; if the logo changes, re-run the brand build over
 * there and paste the new markup in.
 *
 * THE VIEWBOX IS CROPPED TO THE LETTERS, on purpose. The files' own box
 * includes the trail, which overshoots the letters above and below and sits
 * to the left of the O. Boxed that way, the word rode low beside a text label
 * and far from it (Jonathan, 2026-10-15: "a tad lower and there is a space
 * between BY and ORBIT"). Cropping the box to cap-top (y=216) .. baseline
 * (y=488) makes the element's bottom edge the word's baseline, so
 * `align-items: baseline` lines it up with text, and `overflow: visible`
 * still draws the trail outside the box.
 *
 * Decorative: the link around it carries the accessible name.
 */
const TONES = {
  dark: { letters: "#ECE6D9", comet: "#F3D9A0", core: "#FFF7E8" },
  light: { letters: "#0B0E16", comet: "#B08D57", core: "#ECD4A6" },
};

export default function OrbitWordmark({
  tone,
  className,
}: {
  /** The ground it sits on: `dark` for the sidebar, `light` for paper. */
  tone: keyof typeof TONES;
  className?: string;
}) {
  const t = TONES[tone];
  // Gradient and filter ids are document-global; two wordmarks on one page
  // must not share them.
  const uid = `ow${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg
      className={className}
      viewBox="-124.041 216 1049.422 272"
      overflow="visible"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
      <linearGradient id={`${uid}-og`} x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#7A5F33"/>
      <stop offset="50%" stopColor="#ECD4A6"/>
      <stop offset="100%" stopColor="#7A5F33"/>
      </linearGradient>
      <linearGradient id={`${uid}-of`} x1="1" y1="0" x2="0" y2="0">
      <stop offset="0%" stopColor="#B08D57" stopOpacity="0"/>
      <stop offset="42%" stopColor="#B08D57" stopOpacity="0.6"/>
      <stop offset="100%" stopColor="#ECD4A6" stopOpacity="0.8"/>
      </linearGradient>
      <filter id={`${uid}-oc`} x="-120%" y="-120%" width="340%" height="340%">
      <feGaussianBlur stdDeviation="1.300" result="b"/>
      <feMerge>
      <feMergeNode in="b"/>
      <feMergeNode in="SourceGraphic"/>
      </feMerge>
      </filter>
      </defs>
      <g transform="translate(-133.961 70.039) scale(5.591094) translate(50 50) rotate(-18.0)">
      <path d="M 43.75 -4.33 L 42.79 -5.14 L 41.68 -5.93 L 40.40 -6.69 L 38.97 -7.44 L 37.40 -8.15 L 35.68 -8.84 L 33.83 -9.49 L 31.85 -10.10 L 29.75 -10.68 L 27.54 -11.21 L 25.23 -11.71 L 22.82 -12.16 L 20.32 -12.56 L 17.75 -12.92 L 15.11 -13.22 L 12.41 -13.48 L 9.67 -13.69 L 6.89 -13.84 L 4.08 -13.94 L 1.26 -13.99 L -1.57 -13.99 L -4.39 -13.94 L -7.20 -13.83 L -9.97 -13.67 L -12.71 -13.45 L -15.40 -13.19 L -18.04 -12.88 L -20.60 -12.52 L -23.09 -12.11 L -25.49 -11.65 L -27.79 -11.16 L -29.99 -10.61 L -32.08 -10.03 L -34.04 -9.42 L -35.88 -8.76 L -37.58 -8.07 L -39.14 -7.36 L -40.55 -6.61 L -41.81 -5.84 L -42.91 -5.05 L -43.84 -4.24 L -44.62 -3.41 L -45.22 -2.57 L -45.65 -1.72 L -45.91 -0.86 L -46.00 0.00" fill="none" stroke={`url(#${uid}-of)`} strokeWidth="2.0" strokeLinecap="round"/>
      </g>
      <g fill={t.letters} fillRule="nonzero">
      <path transform="translate(0.000 488.000) scale(0.400000 -0.400000)" d="M374 -9Q228 -9 136.0 89.5Q44 188 44 336Q44 483 136.0 581.5Q228 680 374 680Q519 680 611.5 581.5Q704 483 704.0 336.0Q704 189 611.5 90.0Q519 -9 374 -9ZM586 336Q586 443 531.5 508.0Q477 573 375.0 573.0Q273 573 218.0 508.0Q163 443 163 336Q163 228 218.0 163.5Q273 99 375.0 99.0Q477 99 531.5 163.5Q586 228 586 336Z"/>
      <path transform="translate(291.203 488.000) scale(0.400000 -0.400000)" d="M171 0H59V487H171V349Q186 413 225.0 455.0Q264 497 325 497Q372 497 401.5 467.5Q431 438 431 385Q431 325 409 288H294Q325 319 325 364Q325 413 279 413Q236 413 203.5 370.5Q171 328 171 268Z"/>
      <path transform="translate(460.797 488.000) scale(0.400000 -0.400000)" d="M171 0H59V670H171V356Q201 497 327 497Q417 497 469.5 428.5Q522 360 522 243Q522 125 469.0 57.0Q416 -11 325 -11Q201 -11 171 129ZM171 212Q171 80 283 80Q339 80 373.0 121.5Q407 163 407 243Q407 322 372.5 364.0Q338 406 283 406Q171 406 171 273Z"/>
      <path transform="translate(674.797 488.000) scale(0.400000 -0.400000)" d="M171 556H59V670H171ZM171 0H59V487H171Z"/>
      <path transform="translate(758.797 488.000) scale(0.400000 -0.400000)" d="M235 -11Q167 -11 128.5 28.5Q90 68 90 133V400H14V487H90V546L202 617V487H351V400H202V160Q202 94 260 94Q310 94 340 153L375 53Q357 27 320.5 8.0Q284 -11 235 -11Z"/>
      </g>
      <g transform="translate(-133.961 70.039) scale(5.591094) translate(50 50) rotate(-18.0)">
      <path d="M -46.00 0.00 L -45.95 0.63 L -45.82 1.25 L -45.59 1.87 L -45.26 2.49 L -44.85 3.11 L -44.35 3.72 L -43.76 4.32 L -43.08 4.91 L -42.32 5.49 L -41.47 6.06 L -40.53 6.62 L -39.52 7.16 L -38.43 7.70 L -37.26 8.21 L -36.01 8.71 L -34.69 9.19 L -33.31 9.66 L -31.85 10.10 L -30.34 10.52 L -28.76 10.93 L -27.12 11.31 L -25.43 11.67 L -23.69 12.00 L -21.90 12.31 L -20.07 12.60 L -18.20 12.86 L -16.29 13.09 L -14.35 13.30 L -12.38 13.48 L -10.38 13.64 L -8.37 13.77 L -6.33 13.87 L -4.29 13.94 L -2.23 13.98 L -0.17 14.00 L 1.88 13.99 L 3.94 13.95 L 5.99 13.88 L 8.02 13.79 L 10.04 13.66 L 12.04 13.51 L 14.02 13.33 L 15.96 13.13 L 17.88 12.90 L 19.76 12.64 L 21.60 12.36" fill="none" stroke={`url(#${uid}-og)`} strokeWidth="2.6" strokeLinecap="round"/>
      <g transform="translate(21.6 12.4)">
      <circle r="3.4" fill={t.comet} filter={`url(#${uid}-oc)`}/>
      <circle r="1.5" fill={t.core}/>
      </g>
      </g>
    </svg>
  );
}
